import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { useGameStore } from '../../store/gameStore';
import { APPROACH_FIT_CAPTION, TONE_CLASSES, approachFit } from '../../utils/effectiveness';
import { User, Clock, FileText, Lock, MessageSquare } from 'lucide-react';

export const SuspectDetailModal = ({
  suspect,
  open,
  onClose,
  onInterview,
  interviewHistory = [],
  hasBeenInterviewed = false,
}) => {
  const { selectedTheme, isTutorial } = useGameStore();
  const [activeTab, setActiveTab] = useState('profile');

  const themeAccent = {
    noir: '#DC143C',
    '90s_nyc': '#FF6B35',
    miami_vice: '#FF1493',
  }[selectedTheme || 'noir'];

  if (!suspect) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-full h-[100dvh] sm:max-w-2xl lg:max-w-4xl sm:h-[85vh] lg:h-[90vh] bg-gradient-to-br from-slate-900 to-slate-800 border-2 flex flex-col overflow-hidden" style={{ borderColor: themeAccent }}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-white flex items-center gap-2 sm:gap-3 lg:gap-4 pr-10">
            {suspect.portrait ? (
              <img
                src={suspect.portrait}
                alt={suspect.name}
                className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 rounded-full object-cover border-2 flex-shrink-0"
                style={{ borderColor: themeAccent }}
              />
            ) : (
              <div
                className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 rounded-full bg-slate-700 flex items-center justify-center border-2 flex-shrink-0"
                style={{ borderColor: themeAccent }}
              >
                <User className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-slate-400" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-base sm:text-lg lg:text-xl truncate">{suspect.name}</p>
              <p className="text-xs sm:text-sm lg:text-base font-normal truncate" style={{ color: themeAccent }}>
                {suspect.occupation}
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Interview Button - hidden in tutorial mode to prevent breaking flow */}
        {onInterview && !isTutorial && (
          <div className="mt-2 sm:mt-4 flex-shrink-0">
            <Button
              onClick={() => {
                onInterview(suspect.id);
                onClose();
              }}
              className="w-full min-h-touch text-white font-semibold hover:opacity-90 transition-opacity"
              style={{ backgroundColor: themeAccent }}
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Interview {suspect.name}
            </Button>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <TabsList className="grid w-full grid-cols-3 bg-black/40 flex-shrink-0">
            <TabsTrigger value="profile" className="min-h-touch data-[state=active]:bg-white/10 flex-col sm:flex-row gap-0.5 sm:gap-2">
              <User className="w-4 h-4 lg:w-5 lg:h-5" />
              <span className="text-[10px] sm:text-sm lg:text-base">Profile</span>
            </TabsTrigger>
            <TabsTrigger value="alibi" className="min-h-touch data-[state=active]:bg-white/10 flex-col sm:flex-row gap-0.5 sm:gap-2">
              <Clock className="w-4 h-4 lg:w-5 lg:h-5" />
              <span className="text-[10px] sm:text-sm lg:text-base">Alibi</span>
            </TabsTrigger>
            <TabsTrigger value="interviews" className="min-h-touch data-[state=active]:bg-white/10 flex-col sm:flex-row gap-0.5 sm:gap-2">
              <MessageSquare className="w-4 h-4 lg:w-5 lg:h-5" />
              <span className="text-[10px] sm:text-sm lg:text-base">Interviews</span>
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="flex-1 mt-2 sm:mt-4 lg:mt-6 overflow-hidden">
            <ScrollArea className="h-full pr-2 sm:pr-4">
              <div className="space-y-4 lg:space-y-6">
                {/* Basic Info */}
                <Card className="bg-black/30 border-white/10 p-4 lg:p-6">
                  <h3 className="text-sm lg:text-base font-semibold text-gray-400 uppercase mb-3 lg:mb-4">Basic Information</h3>
                  <div className="grid grid-cols-2 gap-4 lg:gap-6">
                    <div>
                      <p className="text-xs lg:text-sm text-gray-500">Age</p>
                      <p className="text-white lg:text-lg">{suspect.age || 'Unknown'}</p>
                    </div>
                    <div>
                      <p className="text-xs lg:text-sm text-gray-500">Occupation</p>
                      <p className="text-white lg:text-lg">{suspect.occupation}</p>
                    </div>
                  </div>
                </Card>

                {/* Relationship to Victim */}
                {suspect.relationship && (
                  <Card className="bg-black/30 border-white/10 p-4 lg:p-6">
                    <h3 className="text-sm lg:text-base font-semibold text-gray-400 uppercase mb-3 lg:mb-4">Relationship to Victim</h3>
                    <p className="text-gray-300 lg:text-lg">{suspect.relationship}</p>
                  </Card>
                )}

                {/* Background Story - Locked until interviewed */}
                <Card className={`bg-black/30 border-white/10 p-4 lg:p-6 ${!hasBeenInterviewed ? 'opacity-60' : ''}`}>
                  <h3 className="text-sm lg:text-base font-semibold text-gray-400 uppercase mb-3 lg:mb-4 flex items-center gap-2">
                    <FileText className="w-4 h-4 lg:w-5 lg:h-5" />
                    Background
                    {!hasBeenInterviewed && (
                      <Lock className="w-3 h-3 lg:w-4 lg:h-4 text-yellow-500" />
                    )}
                  </h3>

                  {hasBeenInterviewed ? (
                    <p className="text-gray-300 lg:text-lg whitespace-pre-wrap">
                      {suspect.background || 'No detailed background available.'}
                    </p>
                  ) : (
                    <div className="text-center py-4 lg:py-6">
                      <Lock className="w-8 h-8 lg:w-10 lg:h-10 text-yellow-500 mx-auto mb-2" />
                      <p className="text-yellow-500 text-sm lg:text-base font-semibold">Background Locked</p>
                      <p className="text-gray-400 text-xs lg:text-sm mt-1">
                        Interview this suspect to unlock their background story
                      </p>
                    </div>
                  )}
                </Card>
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Alibi Tab */}
          <TabsContent value="alibi" className="flex-1 mt-2 sm:mt-4 lg:mt-6 overflow-hidden">
            <ScrollArea className="h-full pr-2 sm:pr-4">
              {/* Alibi is only revealed after interviewing/interrogating */}
              {hasBeenInterviewed ? (
                <Card className="bg-black/30 border-white/10 p-4 lg:p-6">
                  <h3 className="text-sm lg:text-base font-semibold text-gray-400 uppercase mb-3 lg:mb-4 flex items-center gap-2">
                    <Clock className="w-4 h-4 lg:w-5 lg:h-5" style={{ color: themeAccent }} />
                    Stated Alibi
                  </h3>

                  {suspect.alibi ? (
                    <p className="text-gray-300 lg:text-lg whitespace-pre-wrap">{suspect.alibi}</p>
                  ) : (
                    <p className="text-gray-400 lg:text-lg italic">No alibi provided</p>
                  )}
                </Card>
              ) : (
                <Card className="bg-black/30 border-white/10 p-4 lg:p-6">
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <Lock className="w-12 h-12 text-gray-500 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-400 mb-2">Alibi Unknown</h3>
                    <p className="text-gray-500 mb-4">Interview or interrogate this suspect to learn their alibi.</p>
                    {onInterview && (
                      <Button
                        onClick={() => onInterview(suspect)}
                        className="mt-2"
                        style={{ backgroundColor: themeAccent }}
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Interview Suspect
                      </Button>
                    )}
                  </div>
                </Card>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Interviews Tab */}
          <TabsContent value="interviews" className="flex-1 mt-2 sm:mt-4 lg:mt-6 overflow-hidden">
            <ScrollArea className="h-full pr-2 sm:pr-4">
              {interviewHistory.length === 0 ? (
                <div className="text-center text-gray-400 py-8 lg:py-12">
                  <MessageSquare className="w-12 h-12 lg:w-16 lg:h-16 mx-auto mb-3 opacity-30" />
                  <p className="lg:text-lg">No interviews conducted with this suspect</p>
                  <p className="text-sm lg:text-base mt-2">Use the Interview action to question them</p>
                </div>
              ) : (
                <div className="space-y-6 lg:space-y-8">
                  {/* Group interviews by round number */}
                  {(() => {
                    // Group by roundNumber, using index+1 as fallback
                    const rounds = {};
                    interviewHistory.forEach((interview, idx) => {
                      const roundNum = interview.roundNumber || idx + 1;
                      if (!rounds[roundNum]) {
                        rounds[roundNum] = [];
                      }
                      rounds[roundNum].push(interview);
                    });

                    // Sort round numbers and render each round as a section
                    return Object.keys(rounds)
                      .sort((a, b) => Number(a) - Number(b))
                      .map((roundNum) => {
                        const roundInterviews = rounds[roundNum];
                        // Use the first interview's data for the round header
                        const firstInterview = roundInterviews[0];
                        // Per round, as the layout already is: a label the
                        // player can act on rather than a bare percentage.
                        const roundFit = approachFit(firstInterview.effectiveness);

                        return (
                          <div key={roundNum} className="space-y-3 lg:space-y-4">
                            {/* Round Header */}
                            <div
                              className="flex items-center justify-between p-3 lg:p-4 rounded-lg border-2"
                              style={{ borderColor: themeAccent, backgroundColor: `${themeAccent}15` }}
                            >
                              <div className="flex items-center gap-2 lg:gap-3">
                                <MessageSquare className="w-5 h-5 lg:w-6 lg:h-6" style={{ color: themeAccent }} />
                                <span className="text-base lg:text-lg font-bold text-white">
                                  Round {roundNum}
                                </span>
                                {firstInterview.approach && (
                                  <span
                                    className="text-xs lg:text-sm px-2 py-1 rounded-full text-white capitalize"
                                    style={{ backgroundColor: themeAccent }}
                                  >
                                    {firstInterview.approach}
                                  </span>
                                )}
                              </div>
                              {/* The tutorial is out of scope for P3. Its
                                  history comes from `messageHistory` (nothing
                                  writes `interrogationDialogues` outside
                                  `ActionBar`), so `effectiveness` is undefined
                                  and this renders nothing — exactly as before.
                                  The branch keeps that true if the tutorial
                                  ever starts carrying the field. */}
                              {isTutorial ? (
                                firstInterview.effectiveness !== undefined && (
                                  <div className="flex items-center gap-2">
                                    <div className="w-20 lg:w-28 h-2 lg:h-3 bg-gray-700 rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-gradient-to-r from-red-500 via-yellow-500 to-green-500"
                                        style={{ width: `${firstInterview.effectiveness * 100}%` }}
                                      />
                                    </div>
                                    <span className="text-sm lg:text-base font-semibold text-white">
                                      {Math.round(firstInterview.effectiveness * 100)}%
                                    </span>
                                  </div>
                                )
                              ) : roundFit && (
                                <div className="flex flex-col items-end" data-testid="approach-fit">
                                  <span className={`text-sm lg:text-base font-semibold ${TONE_CLASSES[roundFit.tone].text}`}>
                                    {roundFit.label}
                                  </span>
                                  <span className="text-xs text-gray-400 hidden sm:block">
                                    {APPROACH_FIT_CAPTION}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Round Content */}
                            {roundInterviews.map((interview, idx) => (
                              <Card key={idx} className="bg-black/30 border-white/10 p-4 lg:p-6 ml-2 lg:ml-4 border-l-2" style={{ borderLeftColor: themeAccent }}>
                                <p className="text-gray-300 whitespace-pre-wrap text-sm lg:text-base leading-relaxed">
                                  {interview.narrative}
                                </p>
                                {interview.timestamp && (
                                  <p className="text-xs lg:text-sm text-gray-500 mt-3 lg:mt-4 text-right">
                                    {new Date(interview.timestamp).toLocaleString()}
                                  </p>
                                )}
                              </Card>
                            ))}
                          </div>
                        );
                      });
                  })()}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default SuspectDetailModal;
